import { Vector3 } from "three";
import { BASE_RES, GOLDEN_ANGLE, bandIndices, makeCellId, pointCount, pointLatLng, spacingRad } from "./fib-grid";

// The news grid gets finer as the camera comes down: from orbit ~41k points
// (~70 km apart), then ~288k and ~2M (~10 km, city scale; article locations
// aren't more precise than that), each level ~2.65x finer in spacing (see
// fib-grid.js), so levels change every ~2.65x in altitude and dots keep their
// spacing on screen. Zoomed-in levels cover only the points on screen.

export { BASE_RES, cellScale } from "./fib-grid";
// Altitude (globe radii above the surface) below which each finer level
// applies; hysteresis keeps the level from flickering at a boundary.
const LEVELS = [
  { res: 4, below: 0.6 },
  { res: 5, below: 0.22 },
];
const HYSTERESIS = 1.12;
const DEG = Math.PI / 180;

export function gridLevelFor(altitude, currentRes = BASE_RES) {
  let res = BASE_RES;
  for (const { res: r, below } of LEVELS) {
    // Easier to stay at a level than to enter it.
    const threshold = currentRes >= r ? below * HYSTERESIS : below / HYSTERESIS;
    if (altitude < threshold) res = r;
  }
  return res;
}

// Points at `res` that are on screen (with a margin) and on the visible side
// of the globe. project(lat, lng, dir) returns normalised device coordinates
// (Vector3) of a surface point and writes its outward direction to dir.
export function regionCells({ camera, radius, toGeo, project, res, margin = 1.3 }) {
  const camPos = camera.position;
  const altitude = camPos.length() / radius - 1;
  const { lat, lng } = toGeo(camPos);
  // Angular radius to search: the screen's footprint (half-diagonal of the
  // view, looking straight down), capped at the horizon.
  const tanV = Math.tan((camera.fov * Math.PI) / 360);
  const tanD = Math.hypot(tanV, tanV * camera.aspect);
  const horizon = Math.acos(1 / (1 + altitude));
  const angle = Math.min(horizon, altitude * tanD * margin + 0.02);
  // Points are ordered by latitude, so the search circle's latitude band is
  // one run of indices; a dot-product test against the view direction keeps
  // just the points inside the circle before any projection.
  const latRad = lat * DEG;
  const zLo = latRad - angle <= -Math.PI / 2 ? -1 : Math.sin(latRad - angle);
  const zHi = latRad + angle >= Math.PI / 2 ? 1 : Math.sin(latRad + angle);
  const [first, last] = bandIndices(res, zLo, zHi);
  const cosCap = Math.cos(angle);
  const camXY = Math.cos(latRad);
  const camZ = Math.sin(latRad);
  const camLng = lng * DEG;
  const n = pointCount(res);
  const camDir = camPos.clone().normalize();
  const up = new Vector3();
  const cells = [];
  for (let i = first; i <= last; i++) {
    // Cheap test first (one cos per point): the angle to the view direction,
    // from the point's z and longitude.
    const zi = 1 - (2 * i + 1) / n;
    const dot = zi * camZ + Math.sqrt(1 - zi * zi) * camXY * Math.cos(i * GOLDEN_ANGLE - camLng);
    if (dot < cosCap) continue;
    const [cLat, cLng] = pointLatLng(res, i);
    const ndc = project(cLat, cLng, up);
    if (Math.abs(ndc.x) > margin || Math.abs(ndc.y) > margin || ndc.z > 1) continue;
    if (up.dot(camDir) < 1 / (1 + altitude) - 0.02) continue; // beyond the horizon
    cells.push(makeCellId(res, i));
  }
  // Rebuilt when the view centre moves about a fifth of the view or the
  // altitude changes by ~30%.
  return { cells, key: regionKey(res, lat, lng, altitude) };
}

export function regionKey(res, lat, lng, altitude) {
  // The view centre snapped to a lat/lng lattice ten point spacings across
  // (longitude widened towards the poles to keep the steps equal on the ground).
  const stepDeg = (10 * spacingRad(res)) / DEG;
  const lngStep = stepDeg / Math.max(0.2, Math.cos(lat * DEG));
  return `${res}:${Math.round(lat / stepDeg)}:${Math.round(lng / lngStep)}:${Math.round(Math.log(altitude) * 4)}`;
}
